import { Grid, IconButton } from '@mui/material';
import { useEffect, useMemo } from 'react';
import { useFieldArray, useFormContext } from 'react-hook-form';
import DeleteIcon from '@mui/icons-material/Delete';
import { StorageFileArea } from '~/services/api/enums';
import usePatientEnrollmentOptions from '~/ui/components/providers/PatientEnrollmentProvider/usePatientEnrollment';
import DropStorageFilesContainer from '~/ui/components/reusable/DropStorageFilesContainer';

import styles from './DocumentsForm.module.scss';
import { IPatientCreateFormValues } from '../../types';
import Select from '~/ui/components/inputs/Select';

const DocumentsForm = () => {
  const {
    storageFilesSelectors: { storageFileTypes, documentTypes },
    storageFiles,
    onStorageFileUploaded,
    onStorageFileRemove,
  } = usePatientEnrollmentOptions();

  const {
    control,
    formState: { errors },
  } = useFormContext<IPatientCreateFormValues>();

  const { fields, append, remove } = useFieldArray<IPatientCreateFormValues, 'storageFiles', ''>({
    control,
    name: 'storageFiles',
    keyName: '',
  });

  const documentTypeOptions = useMemo(
    () =>
      documentTypes.map(item => ({
        value: item.id,
        label: item.name,
      })),
    [documentTypes],
  );

  useEffect(() => {
    fields
      .map((field, index) => ({ field, index }))
      .reverse()
      .forEach(({ field, index }) => {
        if (!storageFiles.some(x => x.id === field.id)) {
          remove(index);
        }
      });

    storageFiles.forEach(storageFile => {
      if (!fields.some(x => x.id === storageFile.id)) {
        append(storageFile);
      }
    });
  }, [storageFiles]);

  return (
    <Grid container spacing={2}>
      <Grid item xs={12}>
        <DropStorageFilesContainer
          storageFileArea={StorageFileArea.PatientDocuments}
          uploadedCount={storageFiles.length}
          storageFileTypes={storageFileTypes}
          onStorageFileUploaded={onStorageFileUploaded}
          height={200}
          smallHeight={200}
        />
      </Grid>

      {storageFiles.length > 0 && (
        <>
          <Grid item className={styles.uploaded} xs={12}>
            Uploaded
          </Grid>

          <Grid item xs={12}>
            <Grid container spacing={2}>
              {storageFiles.map(storageFile => {
                const index = fields.findIndex(x => x.id === storageFile.id);

                return (
                  <Grid item xs={12} key={storageFile.id}>
                    <Grid container spacing={1}>
                      <Grid item xs={4}>
                        <Select
                          name={`storageFiles.${index}.documentTypeId`}
                          control={control}
                          errors={errors}
                          options={documentTypeOptions}
                          label="Document Type"
                          required
                          isInBody
                        />
                      </Grid>
                      <Grid item xs>
                        <Grid
                          container
                          className={styles.container}
                          alignItems="center"
                          justifyContent="space-between"
                          wrap="nowrap"
                        >
                          <Grid item className={styles.name}>
                            {storageFile.name}
                          </Grid>
                          <Grid item>
                            <IconButton
                              color="secondary"
                              title="Remove"
                              onClick={() => onStorageFileRemove(storageFile)}
                            >
                              <DeleteIcon />
                            </IconButton>
                          </Grid>
                        </Grid>
                      </Grid>
                    </Grid>
                  </Grid>
                );
              })}
            </Grid>
          </Grid>
        </>
      )}
    </Grid>
  );
};

export default DocumentsForm;
